import { PocButton } from 'commons/components/components-wrapper/poc-button.component';
import type { PocInputError } from 'commons/types';
import {
    ConfirmationModalComponent,
    WorkflowOverlayComponent,
    WorkflowOverlayContentComponent,
} from 'commons/components';
import { useDeviceTypes } from 'core/contexts/device-types/hooks/use-device-types.hook';
import useCheckUnsavedChanges from 'core/custom-hooks/use-check-unsaved-changes.hook';
import { useTranslation } from 'core/i18n/i18n';
import { showSuccessNotification } from 'core/utils/notification-service';
import { useConfigurationValidation } from 'modules/settings/contexts/device-configuration/hooks/use-device-configuration-validation.hook';
import { useDeviceConfiguration } from 'modules/settings/contexts/device-configuration/hooks/use-device-configuration.hook';
import { useCallback, useEffect, useState } from 'react';
import { usePopupManager } from 'react-popup-manager';
import { useNavigate, useParams } from 'react-router-dom';
import { ERRORS_MAPPINGS, LAYOUT_WORKFLOW_TABS } from './constants';
import { LayoutWorkflowTabsType } from './settings-devices-configuration-layout.model';
import { DeviceConfigurationContent, DeviceConfigurationHeader } from './workflow';

export const SettingsDevicesConfigurationWorkflow: React.FC = () => {
    const [selectedTab, setSelectedTab] = useState<LayoutWorkflowTabsType>(LAYOUT_WORKFLOW_TABS.DEVICE_PROPERTIES);
    const popupManager = usePopupManager();
    const { t } = useTranslation();

    const {
        hasConfigChanged,
        saveDeviceConfiguration,
        setDeviceConfiguration,
        isLoading,
        deviceConfigurationInstance,
    } = useDeviceConfiguration();
    const { isConfigurationValid, setValidation } = useConfigurationValidation();
    const navigate = useNavigate();

    const handleOnClose = useCallback(() => {
        setTimeout(() => {
            const currentPath = location.pathname;
            const pathSegments = currentPath.split('/');
            const newPath = pathSegments.slice(0, -1).join('/');
            navigate(newPath);
        }, 100);
    }, [navigate]);

    const unsavedData = useCheckUnsavedChanges(handleOnClose);

    useEffect(() => {
        unsavedData.setIsUnsavedChanges(hasConfigChanged);
    }, [hasConfigChanged]);

    const { id } = useParams();
    const { getDeviceTypeDetailsBySlug } = useDeviceTypes();
    const deviceType = getDeviceTypeDetailsBySlug(id ?? '');

    const executeSaveConfiguration = async () => {
        try {
            await saveDeviceConfiguration();
            showSuccessNotification(
                t('settings.devices.device-settings.device-config-layout.action.save.notification.positive')
            );
            handleOnClose();
        } catch (error) {
            try {
                const parsedError = JSON.parse((error as Error).message);
                const errorCode = parsedError.errors[0]?.extensions?.code;
                const errorIdentified = ERRORS_MAPPINGS.find((mapping) => mapping.errorCode === errorCode);

                if (errorIdentified) {
                    setValidation(errorIdentified.field ?? '', {
                        state: 'error',
                        type: [errorIdentified.type] as unknown as PocInputError[],
                    });
                }
            } catch {
                // error message is not valid JSON — no field-level validation possible
            }
        }
    };

    const handleOnSave = () => {
        popupManager.open(ConfirmationModalComponent, {
            title: t('settings.devices.device-settings.device-config-layout.action.save.confirmation-modal.title'),
            children: t(
                'settings.devices.device-settings.device-config-layout.action.save.confirmation-modal.description'
            ),
            buttonText: t(
                'settings.devices.device-settings.device-config-layout.action.save.confirmation-modal.button'
            ),
            actionOnClick: executeSaveConfiguration,
        });
    };

    return (
        <WorkflowOverlayComponent
            visible
            headerTitle={t('settings.devices.device-settings-device-config-layout.workflow.title')}
            onClose={() => unsavedData.showUnsavedChangesIfNeededOrClose()}
            className="show-header"
            footer={[
                <PocButton
                    key={'save-button'}
                    data-testid="device-configuration-save-button"
                    onClick={handleOnSave}
                    disabled={isLoading || !hasConfigChanged || !isConfigurationValid}
                >
                    {t('settings.devices.device-settings.device-config-layout.action.save')}
                </PocButton>,
            ]}
        >
            <WorkflowOverlayContentComponent>
                <DeviceConfigurationHeader
                    deviceConfigurationInstance={deviceConfigurationInstance}
                    deviceType={deviceType}
                    selectedTab={selectedTab}
                    onTabChange={setSelectedTab}
                />
                <DeviceConfigurationContent
                    deviceConfigurationInstance={deviceConfigurationInstance}
                    selectedTab={selectedTab}
                    onConfigurationChange={setDeviceConfiguration}
                />
            </WorkflowOverlayContentComponent>
        </WorkflowOverlayComponent>
    );
};
